/**
 * Carga inicial del demo store. Corre una vez al primer import (vía router.ts).
 * En memoria — cada reload de la página re-inicializa con los seeds.
 *
 * `resetDemo()` queda expuesto en `window.__demoStore.reset()` para debug.
 */
import { ensureSeeded, clearAll } from './store'

import { TENANT_DEMO } from './seeds/tenant'
import { ME_DEMO } from './seeds/me'
import { PRODUCTS_DEMO } from './seeds/products'
import { ORDERS_DEMO } from './seeds/orders'
import { CUSTOMERS_DEMO } from './seeds/customers'
import { INVENTORY_DEMO } from './seeds/inventory'
import { CATEGORIES_DEMO } from './seeds/categories'
import { PRODUCT_TYPES_DEMO } from './seeds/product-types'
import { COUPONS_DEMO } from './seeds/coupons'
import { LOYALTY_TIERS_DEMO, LOYALTY_CODES_DEMO, LOYALTY_MEMBERS_DEMO } from './seeds/loyalty'
import { BLOG_DEMO } from './seeds/blog'
import { COMMENTS_DEMO, REVIEWS_DEMO } from './seeds/social'
import { RETURNS_DEMO } from './seeds/returns'
import { PAYMENTS_DEMO } from './seeds/payments'
import { AUDIT_DEMO } from './seeds/audit'
import { EMAIL_TEMPLATES_DEMO } from './seeds/email-templates'
import { API_KEYS_DEMO } from './seeds/api-keys'
import { SETTINGS_DEMO } from './seeds/settings'

/**
 * Bumpear si cambia el shape de algún seed: fuerza limpiar memoria y
 * re-sembrar para evitar que data vieja (con shape distinto) tire la UI
 * tras un HMR. El usuario nunca lo nota — al recargar arranca limpio igual.
 */
const SCHEMA_VERSION = '6'

let booted = false

function seedAll(): void {
  ensureSeeded('tenant', TENANT_DEMO, 'singleton')
  ensureSeeded('me', ME_DEMO, 'singleton')

  ensureSeeded('products', PRODUCTS_DEMO)
  ensureSeeded('orders', ORDERS_DEMO)
  ensureSeeded('customers', CUSTOMERS_DEMO)
  ensureSeeded('inventory', INVENTORY_DEMO)
  ensureSeeded('categories', CATEGORIES_DEMO)
  ensureSeeded('productTypes', PRODUCT_TYPES_DEMO)
  ensureSeeded('coupons', COUPONS_DEMO)
  ensureSeeded('loyaltyTiers', LOYALTY_TIERS_DEMO)
  ensureSeeded('loyaltyCodes', LOYALTY_CODES_DEMO)
  ensureSeeded('loyaltyMembers', LOYALTY_MEMBERS_DEMO)
  ensureSeeded('blogPosts', BLOG_DEMO)
  ensureSeeded('comments', COMMENTS_DEMO)
  ensureSeeded('reviews', REVIEWS_DEMO)
  ensureSeeded('returns', RETURNS_DEMO)
  ensureSeeded('payments', PAYMENTS_DEMO)
  ensureSeeded('auditLog', AUDIT_DEMO)
  ensureSeeded('emailTemplates', EMAIL_TEMPLATES_DEMO)
  ensureSeeded('apiKeys', API_KEYS_DEMO)
  ensureSeeded('settings', SETTINGS_DEMO)
}

export function bootstrapDemoStore(): void {
  // Schema version: si cambió, limpiar TODO antes de sembrar.
  const w = typeof window !== 'undefined'
    ? (window as unknown as { __demoSchemaV?: string })
    : null
  const stored = w?.__demoSchemaV
  if (stored !== SCHEMA_VERSION) {
    clearAll()
    if (w) w.__demoSchemaV = SCHEMA_VERSION
    booted = false
  }
  if (booted) return
  booted = true
  seedAll()
}

/** Restaura todos los seeds. Útil si querés un botón "Reset demo". */
export function resetDemo(): void {
  clearAll()
  seedAll()
}

if (typeof window !== 'undefined') {
  ;(window as unknown as { __demoStore?: unknown }).__demoStore = {
    reset: resetDemo,
  }
}
